import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ButtonBase from '@material-ui/core/ButtonBase';

import Menu, { MenuItem } from 'components/common/Menu';
import IconTriangleTop from 'components/icons/IconTriangleTop';
import IconTriangleBottom from 'components/icons/IconTriangleBottom';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  root: {
    position: 'relative',
    maxWidth: '100%',
    margin: 0,
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: '700',
    color: theme.palette.grey[600],
    transform: 'none',
  },
  selectRoot: {
    display: 'block',
    padding: 0,
    width: '100%',
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
  },
  selectSelect: {
    width: 'auto',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    lineHeight: 'calc(1em + 14px)',
    height: 'calc(1em + 14px)',
    cursor: 'pointer',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    border: `1px solid ${theme.palette.grey[600]}`,
    userSelect: 'none',
    borderRadius: 4,
    color: theme.palette.secondary[800],
    padding: '0 35px 0 15px',
    '&:focus': {
      borderRadius: 4,
      borderColor: theme.palette.primary[500],
      backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
    },
  },
  selectSelectSm: {
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.25),
    lineHeight: `calc(${pxToEm(14, 13)} + 14px)`,
    height: `calc(${pxToEm(14, 13)} + 14px)`,
  },
  selectSelectMd: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
  },
  selectSelectLg: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
    paddingTop: 3,
    paddingBottom: 4,
  },
  selectSelectXl: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
    paddingTop: 3,
    paddingBottom: 3,
  },
  selectSelectDark: {
    backgroundColor: theme.palette.grey[100],
  },
  selectSelectLight: {
    backgroundColor: theme.palette.common.white,
  },
  selectSelectActive: {
    borderColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
  },
  selectSelectDisabled: {
    color: `rgba(${hexToRgb(theme.palette.secondary[800])}, 0.4)`,
  },
  selectSelectError: {
    borderColor: theme.palette.validation[500],
  },
  selectAdorment: {
    position: 'absolute',
    top: '50%',
    right: 13,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
  },
  iconTriangle: {
    display: 'block',
    width: 10,
    height: 5,
    margin: '5px 0',
    color: theme.palette.grey[600],
  },
  menuItem: {
    fontSize: pxToRem(14),
    lineHeight: pxToEm(20, 14),
    boxSizing: 'border-box',
    height: 'auto',
    textAlign: 'left',
    padding: '10px 15px',
    minWidth: 0,
    maxWidth: 'none',
    borderTop: 'none',
  },
});

class SelectMenuField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      options: props.options,
      anchorEl: null,
      anchorElWidth: 0,
      offset: null,
    };

    this.onScrollOptionsFetchRequested = this.onScrollOptionsFetchRequested.bind(this);
    this.onLabelClick = this.onLabelClick.bind(this);
    this.onSelectRootClick = this.onSelectRootClick.bind(this);
    this.onSelectRootKeyDown = this.onSelectRootKeyDown.bind(this);
    this.onSelectRootBlur = this.onSelectRootBlur.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onMenuItemClick = this.onMenuItemClick.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { input, options, fetchOptions } = this.props;
    const newState = {};
    if (nextProps.input.value !== input.value) {
      newState.anchorEl = null;
    }

    if (nextProps.options !== options && !fetchOptions) {
      newState.options = nextProps.options;
    }
    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      limit,
      fetchOptions,
    } = this.props;
    const { options, offset } = this.state;
    if (fetchOptions && offset !== null && offset !== prevState.offset) {
      fetchOptions({ limit, offset }).then((newOptions) => {
        this.setState({
          options: [
            ...(options && options.length > 0)
              ? options.slice(0, options.length)
              : [],
            ...newOptions.slice(0, newOptions.length),
          ].filter((elem, pos, arr) =>
            arr.findIndex(item => item.value === elem.value) === pos),
        });
      });
    }
  }

  onScrollOptionsFetchRequested() {
    const { limit } = this.props;
    const { options, offset } = this.state;
    if ((options.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  onLabelClick() {
    this.buttonRef.focus();
  }

  onSelectRootClick(event) {
    const { offset } = this.state;
    this.setState({
      anchorEl: event.currentTarget,
      anchorElWidth: event.currentTarget.getBoundingClientRect().width,
      ...offset === null ? { offset: 0 } : {},
    });
  }

  onSelectRootKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({
        anchorEl: event.currentTarget,
        anchorElWidth: event.currentTarget.getBoundingClientRect().width,
      });
    }
  }

  onSelectRootBlur() {
    if (!this.state.anchorEl) {
      this.props.input.onBlur();
    }
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onMenuItemClick(value) {
    this.props.input.onChange(value);
    this.setState({ anchorEl: null });
  }

  render() {
    const {
      className,
      input,
      meta,
      placeholder,
      id,
      label,
      classes,
      size,
      color,
      disabled,
      onListAdornmentClick,
      listAdornmentText,
      listWidth,
      fullWidth,
    } = this.props;
    const { options, anchorEl, anchorElWidth } = this.state;
    const open = Boolean(anchorEl);
    const selectedOption = options
      ? options.find(option => option.value === input.value)
      : null;
    const loading =
      !options
      || options.length === 0;
    return (
      <FormControl
        className={classNames(classes.root, className)}
        fullWidth={fullWidth}
      >
        {label.length > 0 &&
          <InputLabel
            className={classNames(classes.label)}
            disableAnimation
            htmlFor={input.id}
            shrink={false}
            onClick={this.onLabelClick}
          >
            {label}
          </InputLabel>
        }
        <ButtonBase
          className={classes.selectRoot}
          onClick={this.onSelectRootClick}
          onFocus={input.onFocus}
          onBlur={this.onSelectRootBlur}
          onKeyDown={this.onSelectRootKeyDown}
          disabled={disabled}
          disableRipple
          id={id}
          aria-label="Selectmenu"
          aria-owns={open ? `${id}-selectmenu` : null}
          aria-haspopup="true"
          buttonRef={(buttonRef) => { this.buttonRef = buttonRef; }}
        >
          <div
            className={classNames(classes.selectSelect, {
              [classes.selectSelectSm]: size === 'sm',
              [classes.selectSelectMd]: size === 'md',
              [classes.selectSelectLg]: size === 'lg',
              [classes.selectSelectXl]: size === 'xl',
              [classes.selectSelectLight]: color === 'light',
              [classes.selectSelectDark]: color === 'dark',
              [classes.selectSelectActive]: meta.active,
              [classes.selectSelectDisabled]: disabled,
              [classes.selectSelectError]: meta.touched && meta.error,
            })}
          >
            {(selectedOption && selectedOption.label)
              ? selectedOption.label
              : placeholder
            }
          </div>
          <div className={classes.selectAdorment}>
            <IconTriangleTop className={classes.iconTriangle} />
            <IconTriangleBottom className={classes.iconTriangle} />
          </div>
          <input
            {...input}
            type="hidden"
          />
        </ButtonBase>
        <Menu
          id={`${id}-selectmenu`}
          anchorEl={anchorEl}
          open={open}
          onClose={this.onMenuClose}
          arrow={false}
          placement="bottom-left"
          padding="none"
          onListAdornmentClick={onListAdornmentClick}
          listAdornmentText={listAdornmentText}
          onScrollFrame={(event) => {
            if (event.top > 0.8) {
              this.onScrollOptionsFetchRequested();
            }
          }}
        >
          {loading ? (
            <MenuItem
              className={classes.menuItem}
              style={{ width: anchorElWidth }}
            >
              Loadng...
            </MenuItem>
          ) : (
            options.map(option => (
              <MenuItem
                className={classes.menuItem}
                key={option.value}
                value={option.value}
                onClick={() => this.onMenuItemClick(option.value)}
                selected={option.value === input.value}
                {...listWidth === 'inherit' ? {
                  style: {
                    width: anchorElWidth,
                  },
                } : {
                  style: {
                    minWidth: anchorElWidth,
                  },
                }}
              >
                {option.label}
              </MenuItem>
            ))
          )}
        </Menu>
      </FormControl>
    );
  }
}

SelectMenuField.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['light', 'dark']),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fetchOptions: PropTypes.func,
  limit: PropTypes.number,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
  })),
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  placeholder: PropTypes.string,
  onListAdornmentClick: PropTypes.func,
  listAdornmentText: PropTypes.string,
  listWidth: PropTypes.oneOf(['inherit', 'auto']),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selectRoot: PropTypes.string.isRequired,
    selectSelect: PropTypes.string.isRequired,
    selectSelectSm: PropTypes.string.isRequired,
    selectSelectMd: PropTypes.string.isRequired,
    selectSelectLg: PropTypes.string.isRequired,
    selectSelectXl: PropTypes.string.isRequired,
    selectSelectDark: PropTypes.string.isRequired,
    selectSelectLight: PropTypes.string.isRequired,
    selectSelectActive: PropTypes.string.isRequired,
    selectSelectDisabled: PropTypes.string.isRequired,
    selectSelectError: PropTypes.string.isRequired,
    selectAdorment: PropTypes.string.isRequired,
    iconTriangle: PropTypes.string.isRequired,
    menuItem: PropTypes.string.isRequired,
  }).isRequired,
};

SelectMenuField.defaultProps = {
  className: '',
  label: '',
  size: 'lg',
  color: 'light',
  disabled: false,
  fullWidth: false,
  placeholder: '',
  meta: {
    touched: false,
    error: '',
  },
  options: null,
  fetchOptions: null,
  limit: 10,
  onListAdornmentClick: null,
  listAdornmentText: null,
  listWidth: 'inherit',
};

export default withStyles(styles)(SelectMenuField);



// WEBPACK FOOTER //
// ./src/components/fields/SelectMenuField.js